// frontend/modules/template/views/ContextScriptViewView.tsx
import React, { useState } from 'react';
import {
    Typography,
    Button,
    Card,
    Descriptions,
    Tag,
    Divider,
    Spin,
    Space,
    Tabs,
    Form
} from 'antd';
import {
    ArrowLeftOutlined,
    EditOutlined,
    PlayCircleOutlined
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useGetScriptById } from '@/shared/api/client/context-script-controller';
import { ContextScriptEditor } from '../components/ContextScriptEditor';
import { ScriptTestingPanel } from '../components/ScriptTestingPanel';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export const ContextScriptViewView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [testingVisible, setTestingVisible] = useState(false);
    const [form] = Form.useForm();

    const { data: getScriptApi } = useGetScriptById(id || '', { query: { enabled: false } });

    // Load script data
    const { data: scriptResponse, isLoading } = useQuery(
        // @ts-ignore
        ['context-script', id],
        async () => {
            if (id) {
                // @ts-ignore
                return await getScriptApi();
            }
            return null;
        }
    );
    // @ts-ignore
    const script = scriptResponse?.data;

    const handleEdit = () => {
        if (id) {
            navigate(`/notifications/context-scripts/edit/${id}`);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spin size="large" tip="Loading script..." />
            </div>
        );
    }

    if (!script) {
        return (
            <Card>
                <div className="text-center py-8">
                    <Text type="danger">Script not found</Text>
                    <div className="mt-4">
                        <Link to="/notifications/context-scripts">
                            <Button>Back to Script List</Button>
                        </Link>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <div>
            <div className="flex items-center mb-4">
                <Link to="/notifications/context-scripts">
                    <Button icon={<ArrowLeftOutlined />} className="mr-4">
                        Back to Scripts
                    </Button>
                </Link>
                <Title level={2} className="m-0">
                    {script.name}
                </Title>
            </div>

            <Card className="mb-4">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <Space direction="vertical" size="small">
                            <Space>
                                <Tag>{script.category}</Tag>
                                <Tag color={script.active ? 'success' : 'default'}>
                                    {script.active ? 'Active' : 'Inactive'}
                                </Tag>
                            </Space>
                            {script.description && (
                                <Text type="secondary">{script.description}</Text>
                            )}
                        </Space>
                    </div>
                    <Space>
                        <Button
                            icon={<PlayCircleOutlined />}
                            onClick={() => setTestingVisible(true)}
                        >
                            Test Script
                        </Button>
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={handleEdit}
                        >
                            Edit
                        </Button>
                    </Space>
                </div>

                <Descriptions bordered column={1} size="small">
                    <Descriptions.Item label="ID">{script.id}</Descriptions.Item>
                    <Descriptions.Item label="Version">{script.version || '1'}</Descriptions.Item>
                    <Descriptions.Item label="Created By">{script.createdBy || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Created At">
                        {script.createdAt ? new Date(script.createdAt).toLocaleString() : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Updated By">{script.updatedBy || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Updated At">
                        {script.updatedAt ? new Date(script.updatedAt).toLocaleString() : '-'}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <Tabs defaultActiveKey="script">
                <TabPane tab="Script Content" key="script">
                    <Card>
                        <ContextScriptEditor
                            value={script.scriptContent || ''}
                            onChange={() => {}}
                            readOnly
                        />
                    </Card>
                </TabPane>

                <TabPane tab="Sample Input/Output" key="samples">
                    <Card>
                        <div className="mb-6">
                            <Title level={5}>Sample Input</Title>
                            {script.sampleInput ? (
                                <pre className="bg-gray-50 p-4 rounded overflow-auto">
                                    {script.sampleInput}
                                </pre>
                            ) : (
                                <Text type="secondary">No sample input provided</Text>
                            )}
                        </div>

                        <div>
                            <Title level={5}>Expected Output</Title>
                            {script.expectedOutput ? (
                                <pre className="bg-gray-50 p-4 rounded overflow-auto">
                                    {script.expectedOutput}
                                </pre>
                            ) : (
                                <Text type="secondary">No expected output provided</Text>
                            )}
                        </div>
                    </Card>
                </TabPane>
            </Tabs>

            {testingVisible && (
                <ScriptTestingPanel
                    visible={testingVisible}
                    onClose={() => setTestingVisible(false)}
                    scriptContent={script.scriptContent || ''}
                    form={form}
                />
            )}
        </div>
    );
};